import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Container,
  Typography,
  Paper,
  CircularProgress,
  Stack,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import GoogleIcon from '@mui/icons-material/Google';

import ThemeToggle from '../features/theme/components/ThemeToggle';

import { reportAPI, SERVICE_UNAVAILABLE_MESSAGE } from '../services/api';
import { clearFrontendSessionState, OAUTH_REDIRECT_IN_PROGRESS_KEY } from '../utils/sessionCleanup';

const LoginPage: React.FC = () => {
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down('sm'));
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Check if already authenticated (cookie-based session)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const err = (params.get('error') || '').toLowerCase();
    const storedAuthError = localStorage.getItem('auth_error');
    if (err) {
      const message =
        err === 'oidc_not_configured'
          ? 'Google-inloggning är inte tillgänglig just nu.'
          : err === 'missing_code' || err === 'missing_state' || err === 'invalid_state'
            ? 'Inloggningen kunde inte verifieras. Försök igen.'
            : err === 'token_exchange_failed'
              ? 'Det gick inte att slutföra Google-inloggningen. Försök igen.'
              : err === 'missing_id_token' || err === 'invalid_id_token' || err === 'invalid_claims' || err === 'invalid_nonce'
                ? 'Google-inloggningen kunde inte verifieras. Försök igen.'
                : err === 'unauthorized_domain'
                  ? 'Endast ictech.se.'
                  : err === 'unauthorized_user'
                    ? 'Ingen åtkomst.'
                    : err === 'group_check_failed'
                      ? 'Kunde inte verifiera Google Workspace-grupper.'
                      : err === 'no_profile_match'
                        ? 'Ingen matchande profil hittades.'
                        : err === 'profile_lookup_failed'
                          ? 'Det gick inte att hämta profilinformationen.'
                          : err === 'session_failed'
                            ? 'Det gick inte att skapa en session. Försök igen.'
                            : 'Inloggning misslyckades.';
      setError(message);
      window.history.replaceState({}, document.title, window.location.pathname);
    } else if (storedAuthError) {
      setError(storedAuthError);
      localStorage.removeItem('auth_error');
    }

    const checkExistingSession = async () => {
      try {
        const status = await reportAPI.getAuthStatus();
        if (status.authenticated) {
          localStorage.setItem('auth_state', 'authenticated');
        }
      } catch {
        // ignore
      }
    };

    checkExistingSession();
  }, []);

  const googleButtonWidth = isXs ? '100%' : 420;
  const isServiceUnavailable = error === SERVICE_UNAVAILABLE_MESSAGE;

  const handleLogin = async (postLoginPage: 'main' | 'admin' = 'main') => {
    setIsLoading(true);
    setError(null);

    try {
      clearFrontendSessionState();
      localStorage.setItem('post_login_page', postLoginPage);
      const { authUrl } = await reportAPI.getAuthorizationUrl(undefined, undefined, true);
      if (!authUrl) {
        throw new Error('Kunde inte starta.');
      }
      sessionStorage.setItem(OAUTH_REDIRECT_IN_PROGRESS_KEY, '1');
      window.location.href = authUrl;
    } catch (err: any) {
      const status = err?.response?.status;
      if (status === 503) {
        clearFrontendSessionState();
        setError(SERVICE_UNAVAILABLE_MESSAGE);
        setIsLoading(false);
        return;
      } else {
        setError(err instanceof Error ? err.message : 'Inloggning misslyckades.');
      }
      setIsLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: theme.palette.background.default,
      }}
    >
      <Box sx={{ position: 'fixed', top: 16, right: 16 }}>
        <ThemeToggle iconColor={theme.palette.text.primary} />
      </Box>
      <Container maxWidth="sm">
        <Paper
          elevation={6}
          sx={{
            px: { xs: 3, sm: 5 },
            py: { xs: 4, sm: 5 },
            borderRadius: '16px',
            background: theme.palette.background.paper,
            backdropFilter: 'blur(10px)',
            fontFamily: '"Helvetica Neue", Helvetica, Arial, sans-serif',
            maxWidth: 560,
            mx: 'auto',
          }}
        >
          <Stack spacing={2.25} alignItems="center" sx={{ textAlign: 'center' }}>

            {/* Error Message */}
            {error && (
              <Box
                sx={{
                  width: '100%',
                  maxWidth: googleButtonWidth,
                  p: 2,
                  backgroundColor: isServiceUnavailable ? theme.palette.warning.main : theme.palette.error.main,
                  color: isServiceUnavailable ? theme.palette.warning.contrastText : '#fff',
                  borderRadius: '8px',
                  textAlign: 'center',
                }}
              >
                <Typography variant="body2">{error}</Typography>
              </Box>
            )}

            <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
              <Button
                variant="contained"
                size="large"
                startIcon={isLoading ? undefined : <GoogleIcon />}
                onClick={() => handleLogin('main')}
                disabled={isLoading || isServiceUnavailable}
                sx={{
                  width: '100%',
                  maxWidth: googleButtonWidth,
                  minHeight: 58,
                  px: 3,
                  backgroundColor: theme.palette.primary.main,
                  color: '#fff',
                  fontWeight: 700,
                  fontSize: '1rem',
                  borderRadius: '14px',
                  textTransform: 'none',
                  boxShadow: 'none',
                  '& .MuiButton-startIcon': {
                    mr: 1,
                  },
                  '&:hover': {
                    backgroundColor: theme.palette.primary.dark,
                    boxShadow: 'none',
                  },
                  '&:disabled': {
                    opacity: 0.7,
                  },
                }}
              >
                {isLoading ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1.25, width: '100%' }}>
                    <CircularProgress size={20} sx={{ color: '#fff' }} />
                    <span>Loggar in...</span>
                  </Box>
                ) : (
                  'Logga in med Google'
                )}
              </Button>
            </Box>

            {isServiceUnavailable && (
              <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
                <Button
                  variant="outlined"
                  size="large"
                  onClick={() => window.location.reload()}
                  sx={{
                    width: '100%',
                    maxWidth: googleButtonWidth,
                    minHeight: 52,
                    borderRadius: '14px',
                    textTransform: 'none',
                  }}
                >
                  Försök igen
                </Button>
              </Box>
            )}
          </Stack>

        </Paper>

        {/* Footer */}
        <Typography
          variant="caption"
          sx={{
            display: 'block',
            textAlign: 'center',
            color: theme.palette.text.secondary,
            mt: 4,
            opacity: 0.8,
          }}
        >
          © 2026 ICTech.
        </Typography>
      </Container>
    </Box>
  );
};

export default LoginPage;
